import { useMutation, useQuery } from '@tanstack/react-query';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import moment from 'moment';
import {
  deleteNotification,
  getScheduledNotification,
  scheduleFotbollPlayNotification,
  updateScheduledFotbollPlayNotification,
} from '../api/push-notification';
import { FS_PATH_PUSH_NOTIFICATION_CONTENT_CONNECTION } from '../constants';
import { useUserAuth } from '../contexts/auth';
import firebaseApp from '../firebase-app';
import { PushNotificationConnection, SchedulePushdNotificationBody } from '../types/notification';

const getPath = (id: string, abbrv: string) =>
  `${FS_PATH_PUSH_NOTIFICATION_CONTENT_CONNECTION}/${abbrv}/FOTBOLLPLAY/${id}`;

const getFotbollPlayConnection = async (params: {
  id: string;
  abbrv: string;
}): Promise<PushNotificationConnection | null> => {
  const { id, abbrv } = params;
  const docs = await getDoc(doc(firebaseApp.db, getPath(id, abbrv)));
  if (docs.exists()) {
    return docs.data() as PushNotificationConnection;
  }

  return null;
};

export function useFotbollPlayNotificationConf(params: { abbrv: string; id: string }) {
  const { abbrv, id } = params;
  const { currentUser } = useUserAuth();

  const query = useQuery({
    queryKey: ['fotbollplay', 'notification', abbrv, id],
    queryFn: async (): Promise<SchedulePushdNotificationBody | null> => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }

      const fotbollPlayConnection = await getFotbollPlayConnection({
        abbrv,
        id,
      });
      const pushNotificationId = fotbollPlayConnection?.pushNotificationIds?.[0];
      if (!pushNotificationId) {
        return null;
      }

      const fotbollPlayNotification = await getScheduledNotification(
        abbrv,
        pushNotificationId,
        auth
      );

      return fotbollPlayNotification || null;
    },
    refetchOnWindowFocus: false,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: Parameters<typeof scheduleFotbollPlayNotification>[0]['body']) => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }

      const notificationRes = await scheduleFotbollPlayNotification({
        abbrv,
        body: {
          notification: payload.notification,
          id: `${id}-${moment().valueOf()}`,
          scheduledTime: payload.scheduledTime,
        },
        auth,
      });

      if (notificationRes.id) {
        const docRef = doc(firebaseApp.db, getPath(id, abbrv));
        await setDoc(docRef, {
          pushNotificationIds: [notificationRes.id],
        });
      }

      return notificationRes;
    },
    onSuccess() {
      query.refetch();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (
      payload: Parameters<typeof updateScheduledFotbollPlayNotification>[0]['body']
    ) => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }

      const notificationRes = await updateScheduledFotbollPlayNotification({
        abbrv,
        body: {
          notification: payload.notification,
          id: payload.id,
          scheduledTime: payload.scheduledTime,
          createdAt: payload.createdAt,
        },
        auth,
      });

      return notificationRes;
    },
    onSuccess() {
      query.refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (params: { id: string; videoId: string }) => {
      const { id, videoId } = params;
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }

      await deleteNotification(id, auth);

      const docRef = doc(firebaseApp.db, getPath(videoId, abbrv));
      const res = await updateDoc(docRef, {
        pushNotificationIds: null,
      });

      return res;
    },
    onSuccess() {
      query.refetch();
    },
  });

  return {
    query,
    createMutation,
    updateMutation,
    deleteMutation,
  };
}
